/**
 * AskThreadPanel — the Ask mode of the V5 chat drawer (FXC-9009).
 *
 * A local, non-persisted Q&A transcript over the project: questions go to
 * /api/projects/{id}/ask (server-grounded stats + local Ollama, streaming
 * SSE); on the pre-cut-over demo API a dev build falls back to direct local
 * Ollama with client-assembled stats — and SAYS so via the model badge.
 *
 * Deliberately not project chat: answers are ephemeral analyst output, not
 * team messages, so nothing here writes to the chat channel.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { streamAsk, type AskProvider, type AskVia } from "../../api/ask";
import { Icons } from "../thread-v4/icons";

interface AskEntry {
  question: string;
  answer: string;
  status: "streaming" | "done" | "offline" | "error";
  note: string | null;
  model: string | null;
  via: AskVia | null;
  provider: AskProvider;
}

const SUGGESTIONS = [
  "Which runs stalled?",
  "Where is the model least certain?",
  "How many runs are there and what's the CL range?",
];

/** Agent switcher (FXC-10027): same question, pick the model per request.
 *  Ollama runs locally with no key; the hosted providers stream through the
 *  API (which holds the keys) and 503 gracefully when a key is absent. */
const PROVIDERS: Array<{ id: AskProvider; label: string }> = [
  { id: "ollama", label: "Local (Ollama)" },
  { id: "anthropic", label: "Claude" },
  { id: "openai", label: "ChatGPT" },
];

export function AskThreadPanel({ projectId, branch }: { projectId: string; branch: string }) {
  const [entries, setEntries] = useState<AskEntry[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState<AskProvider>("ollama");
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // One in-flight question at a time; abort it on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Keep the newest tokens on screen while streaming.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries]);

  const ask = useCallback((question: string) => {
    const q = question.trim();
    if (!q) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    setDraft("");
    setEntries((prev) => [...prev, { question: q, answer: "", status: "streaming", note: null, model: null, via: null, provider }]);

    const patchLast = (patch: Partial<AskEntry>) =>
      setEntries((prev) => prev.map((e, i) => (i === prev.length - 1 ? { ...e, ...patch } : e)));

    void streamAsk(projectId, branch, q, {
      onModel: (model, via) => patchLast({ model, via }),
      onDelta: (delta) => setEntries((prev) => prev.map((e, i) => (i === prev.length - 1 ? { ...e, answer: e.answer + delta } : e))),
      onOffline: (reason) => {
        patchLast({ status: "offline", note: reason });
        setBusy(false);
      },
      onError: (reason) => {
        patchLast({ status: "error", note: reason });
        setBusy(false);
      },
      onDone: () => {
        patchLast({ status: "done" });
        setBusy(false);
      },
    }, controller.signal, provider).finally(() => {
      if (abortRef.current === controller) setBusy(false);
    });
  }, [projectId, branch, provider]);

  const canSend = !busy && Boolean(draft.trim());

  return (
    <div className="v5-ask-panel" data-testid="ask-thread-panel">
      <div className="v5-chat-list" ref={listRef}>
        {entries.length === 0 ? (
          <div className="v5-ask-empty">
            <p className="v5-muted">
              Ask about this project — answers are grounded in the run table and
              Tailor campaign stats, and stream from a local model.
            </p>
            <div className="v5-ask-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="v5-ask-suggestion" onClick={() => ask(s)} disabled={busy}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          entries.map((entry, i) => (
            <article key={i} className="v5-chat-message v5-ask-entry" data-status={entry.status}>
              <header>
                <span>Q</span>
                <strong>{entry.question}</strong>
              </header>
              <p>
                {entry.answer}
                {entry.status === "streaming" ? <span className="v5-ask-cursor">▍</span> : null}
              </p>
              {entry.status === "offline" || entry.status === "error" ? (
                <small className="v5-ask-note">{entry.note ?? "unavailable"}</small>
              ) : entry.model ? (
                <small className="v5-ask-note">
                  {PROVIDERS.find((p) => p.id === entry.provider)?.label ?? entry.provider} · {entry.model}
                  {entry.via === "ollama-direct" ? " · direct local Ollama (staged API fallback)" : ""}
                </small>
              ) : null}
            </article>
          ))
        )}
      </div>
      <div className="v5-ask-provider-row" role="tablist" aria-label="Agent backend">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={provider === p.id}
            className={`v5-ask-provider${provider === p.id ? " is-active" : ""}`}
            onClick={() => setProvider(p.id)}
            data-testid={`ask-provider-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <form
        className="v5-chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSend) ask(draft);
        }}
      >
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (canSend) ask(draft);
            }
          }}
          placeholder="Ask about this project"
          data-testid="ask-thread-input"
        />
        <button className="v5-primary-button" type="submit" disabled={!canSend}>
          <Icons.Comments size={14} />{busy ? "Thinking" : "Ask"}
        </button>
      </form>
    </div>
  );
}
